import { useState, useEffect } from 'react'
import { useAppSelector } from '../hooks/redux'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { getApiUrl, createFetchConfig } from '../config/api'
import {
  UsersIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  CogIcon,
  FolderIcon,
  UserGroupIcon,
  ArrowUpIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  QueueListIcon
} from '@heroicons/react/24/outline'
import { User } from '@/types'
import GroupManagement from '../components/admin/GroupManagement'
import ProjectManagement from '../components/admin/ProjectManagement'
import RoleManagement from '../components/admin/RoleManagement'
import AuditLogManagement from '../components/admin/AuditLogManagement'
import CustomFieldManagement from '../components/admin/CustomFieldManagement'
import QueueManagement from './QueueManagement'

interface AdminPanelState {
  users: User[]
  loading: boolean
  error: string | null
  editingUser: string | null
  editingField: string | null
  editingValue: string
  inviteEmail: string
  inviteLoading: boolean
  showInviteForm: boolean
  activeTab: 'users' | 'groups' | 'projects' | 'roles' | 'custom-fields' | 'audit-logs' | 'queues'
  showGroupModal: boolean
  editingUserGroups: string | null
  availableGroups: any[]
  selectedGroupIds: string[]
  groupsLoading: boolean
}

interface EditingData {
  name?: string
  email?: string
  enabled?: boolean
  adminNotes?: string
}

const AdminPanel = () => {
  const { user: currentUser } = useAppSelector(state => state.auth)
  const { hasPermission, isAdmin, isSuperAdmin, clearCache, refreshPermissions } = usePermissions()
  
  const [state, setState] = useState<AdminPanelState>({
    users: [],
    loading: true,
    error: null,
    editingUser: null,
    editingField: null,
    editingValue: '',
    inviteEmail: '',
    inviteLoading: false,
    showInviteForm: false,
    activeTab: 'users',
    showGroupModal: false,
    editingUserGroups: null,
    availableGroups: [],
    selectedGroupIds: [],
    groupsLoading: false
  })

  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  useEffect(() => {
    fetchUsers()
    // 清除權限快取並重新載入，確保使用最新的群組權限
    clearCache()
    refreshPermissions()
  }, []) // 只在組件初次加載時執行

  const fetchUsers = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const response = await fetch(getApiUrl('admin/users'), createFetchConfig())
      
      if (!response.ok) {
        throw new Error('獲取使用者列表失敗')
      }
      
      const data = await response.json()
      setState(prev => ({ 
        ...prev, 
        users: data.data?.users || [], 
        loading: false 
      }))
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: error.message || '獲取使用者列表失敗', 
        loading: false 
      }))
    }
  }

  const updateUserRole = async (userId: string, role: 'ADMIN' | 'USER') => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ role })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '更新使用者角色失敗')
      }

      const result = await response.json()
      setMessage({ type: 'success', text: `${result.message || '角色更新成功'}。請提醒該使用者重新登入以更新權限。` })
      await fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '更新角色失敗' })
    }
  }

  const promoteToAdmin = async (userId: string, userName: string) => {
    const confirmed = window.confirm(
      `確定要將使用者「${userName}」提升為管理員嗎？\n\n此操作會給予該使用者管理權限，包括管理其他使用者的能力。`
    )
    
    if (!confirmed) return
    
    await updateUserRole(userId, 'ADMIN')
  }

  const updateUserProfile = async (userId: string, updateData: EditingData) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        throw new Error('更新使用者資料失敗')
      }

      setMessage({ type: 'success', text: '使用者資料更新成功' })
      await fetchUsers()
      setState(prev => ({ ...prev, editingUser: null, editingField: null }))
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '更新資料失敗' })
    }
  }

  const updateUserStatus = async (userId: string, enabled: boolean) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ enabled })
      })

      if (!response.ok) {
        throw new Error('更新使用者狀態失敗')
      }

      setMessage({ type: 'success', text: `使用者已${enabled ? '啟用' : '停用'}` })
      await fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '更新狀態失敗' })
    }
  }

  const updateUserNotes = async (userId: string, adminNotes: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ adminNotes })
      })

      if (!response.ok) {
        throw new Error('更新備註失敗')
      }

      setMessage({ type: 'success', text: '備註更新成功' })
      await fetchUsers()
      setState(prev => ({ 
        ...prev, 
        editingUser: null,
        editingField: null,
        editingValue: ''
      }))
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '更新備註失敗' })
    }
  }

  const deleteUser = async (userId: string) => {
    if (!window.confirm('確定要刪除此使用者嗎？此操作無法復原。')) {
      return
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('刪除使用者失敗')
      }

      setMessage({ type: 'success', text: '使用者已刪除' })
      await fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '刪除使用者失敗' })
    }
  }

  const inviteUser = async () => {
    if (!state.inviteEmail.trim()) {
      setMessage({ type: 'error', text: '請輸入有效的 Email 地址' })
      return
    }

    setState(prev => ({ ...prev, inviteLoading: true }))
    try {
      const response = await fetch(getApiUrl('admin/invite'), {
        ...createFetchConfig({
          method: 'POST',
          body: JSON.stringify({ email: state.inviteEmail })
        })
      })

      if (!response.ok) {
        throw new Error('發送邀請失敗')
      }

      setMessage({ type: 'success', text: '邀請已發送' })
      setState(prev => ({ 
        ...prev, 
        inviteEmail: '', 
        showInviteForm: false,
        inviteLoading: false
      }))
      await fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '發送邀請失敗' })
      setState(prev => ({ ...prev, inviteLoading: false }))
    }
  }

  // ========== 群組管理相關函數 ==========

  const fetchAvailableGroups = async () => {
    try {
      const response = await fetch(getApiUrl('groups'), createFetchConfig())

      if (!response.ok) {
        throw new Error('獲取群組列表失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, availableGroups: data.data?.groups || data.data || [] }))
    } catch (error: any) {
      console.error('獲取群組列表失敗:', error)
      // 確保即使出錯也設定為空陣列
      setState(prev => ({ ...prev, availableGroups: [] }))
    }
  }

  const openGroupModal = async (user: User) => {
    // 每次開啟都重新載入可用群組，避免在「群組管理」新增的群組沒出現在清單中
    setState(prev => ({ ...prev, groupsLoading: true }))
    await fetchAvailableGroups()
    setState(prev => ({ ...prev, groupsLoading: false }))
    
    setState(prev => ({ 
      ...prev, 
      showGroupModal: true, 
      editingUserGroups: user.id,
      selectedGroupIds: (user as any).groups?.map((g: any) => g.group.id) || []
    }))
  }

  const closeGroupModal = () => {
    setState(prev => ({ 
      ...prev, 
      showGroupModal: false, 
      editingUserGroups: null,
      selectedGroupIds: []
    }))
  }

  const updateUserGroups = async () => {
    if (!state.editingUserGroups) return

    setState(prev => ({ ...prev, groupsLoading: true }))
    try {
      const response = await fetch(`/api/admin/users/${state.editingUserGroups}/groups`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ groupIds: state.selectedGroupIds })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '更新使用者群組失敗')
      }

      setMessage({ type: 'success', text: '使用者群組更新成功' })
      closeGroupModal()
      await fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setState(prev => ({ ...prev, groupsLoading: false }))
    }
  }

  const toggleGroupSelection = (groupId: string) => {
    setState(prev => ({
      ...prev,
      selectedGroupIds: prev.selectedGroupIds.includes(groupId)
        ? prev.selectedGroupIds.filter(id => id !== groupId)
        : [...prev.selectedGroupIds, groupId]
    }))
  }


  // 檢查當前使用者是否可以編輯目標使用者
  const canEditUser = (targetUser: User) => {
    // 必須有使用者管理權限
    if (!currentUser || !hasPermission(Permission.MANAGE_USERS)) return false
    // 不能編輯自己
    if (targetUser.id === currentUser.id) return false
    // 不能編輯超級管理員（除非自己也是超級管理員）
    if (isSuperAdminUser(targetUser) && !isSuperAdmin) return false
    return true
  }

  // 檢查是否為超級管理員（只檢查角色）
  const isSuperAdminUser = (user: User) => {
    // 只有 SUPER_ADMIN 角色才是超級管理員
    return user.role === 'SUPER_ADMIN'
  }

  // 檢查是否可以提升使用者權限
  const canPromoteUser = (targetUser: User) => {
    // 必須是超級管理員
    if (!isSuperAdmin) return false
    // 目標使用者必須是普通使用者
    if (targetUser.role !== 'USER') return false
    // 不能提升自己
    if (targetUser.id === currentUser?.id) return false
    // 目標使用者必須是啟用狀態
    if (!targetUser.enabled) return false
    return true
  }

  const startEdit = (userId: string, field: string, currentValue: string) => {
    setState(prev => ({
      ...prev,
      editingUser: userId,
      editingField: field,
      editingValue: currentValue
    }))
  }

  const cancelEdit = () => {
    setState(prev => ({
      ...prev,
      editingUser: null,
      editingField: null,
      editingValue: ''
    }))
  }

  const saveEdit = (userId: string, field: string) => {
    if (field === 'name' || field === 'email') {
      updateUserProfile(userId, { [field]: state.editingValue })
    } else if (field === 'notes') {
      updateUserNotes(userId, state.editingValue)
    }
  }


  if (!currentUser || (!isAdmin && !hasPermission(Permission.MANAGE_USERS))) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
          <p className="mt-1 text-sm text-gray-500">您沒有權限存取管理員後台</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center">
                  <ShieldCheckIcon className="w-6 md:w-8 h-6 md:h-8 mr-2 md:mr-3 text-blue-600" />
                  管理員後台
                </h1>
                <p className="text-sm md:text-base text-gray-600 mt-1">管理系統使用者和權限</p>
              </div>
              {state.activeTab === 'users' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setState(prev => ({ ...prev, showInviteForm: !prev.showInviteForm }))}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center"
                  >
                    <UserPlusIcon className="w-5 h-5 mr-2" />
                    邀請使用者
                  </button>
                  <button
                    onClick={() => {
                      console.log('Current permissions check:')
                      console.log('hasPermission(MANAGE_USERS):', hasPermission(Permission.MANAGE_USERS))
                      console.log('isAdmin:', isAdmin)
                      console.log('isSuperAdmin:', isSuperAdmin)
                      clearCache()
                      refreshPermissions()
                    }}
                    className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm"
                  >
                    🔄 重新載入權限
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-4 md:px-6">
            <nav className="flex space-x-8 -mb-px">
              <button
                onClick={() => setState(prev => ({ ...prev, activeTab: 'users' }))}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  state.activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UsersIcon className="w-5 h-5 inline mr-2" />
                使用者管理
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, activeTab: 'groups' }))}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  state.activeTab === 'groups'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <CogIcon className="w-5 h-5 inline mr-2" />
                群組管理
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, activeTab: 'projects' }))}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  state.activeTab === 'projects'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FolderIcon className="w-5 h-5 inline mr-2" />
                專案管理
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, activeTab: 'roles' }))}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  state.activeTab === 'roles'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserGroupIcon className="w-5 h-5 inline mr-2" />
                角色與權限
              </button>
              {hasPermission(Permission.MANAGE_CUSTOM_FIELDS) && (
                <button
                  onClick={() => setState(prev => ({ ...prev, activeTab: 'custom-fields' }))}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    state.activeTab === 'custom-fields'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <AdjustmentsHorizontalIcon className="w-5 h-5 inline mr-2" />
                  自訂欄位
                </button>
              )}
              {hasPermission(Permission.VIEW_AUDIT_LOGS) && (
                <button
                  onClick={() => setState(prev => ({ ...prev, activeTab: 'audit-logs' }))}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    state.activeTab === 'audit-logs'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <ClockIcon className="w-5 h-5 inline mr-2" />
                  稽核日誌
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setState(prev => ({ ...prev, activeTab: 'queues' }))}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    state.activeTab === 'queues'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <QueueListIcon className="w-5 h-5 inline mr-2" />
                  佇列管理
                </button>
              )}
            </nav>
          </div>

          {/* Message */}
          {message && (
            <div className={`mx-4 md:mx-6 mt-4 p-3 md:p-4 rounded-md ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {message.type === 'success' ? (
                    <CheckIcon className="h-4 md:h-5 w-4 md:w-5 text-green-400" />
                  ) : (
                    <XMarkIcon className="h-4 md:h-5 w-4 md:w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-2 md:ml-3 flex-1">
                  <p className="text-xs md:text-sm font-medium">{message.text}</p>
                </div>
                <div className="ml-2 md:ml-3">
                  <button
                    onClick={() => setMessage(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 md:h-5 w-4 md:w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invite Form */}
          {state.activeTab === 'users' && state.showInviteForm && (
            <div className="px-4 md:px-6 py-4 border-b border-gray-200 bg-blue-50">
              <div className="max-w-full md:max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  邀請新使用者
                </label>
                <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3">
                  <div className="flex-1">
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 md:h-5 w-4 md:w-5 text-gray-400" />
                      <input
                        type="email"
                        value={state.inviteEmail}
                        onChange={(e) => setState(prev => ({ ...prev, inviteEmail: e.target.value }))}
                        placeholder="輸入 Email 地址"
                        className="pl-9 md:pl-10 pr-3 py-2 text-sm md:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                        disabled={state.inviteLoading}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={inviteUser}
                      disabled={state.inviteLoading}
                      className="flex-1 md:flex-none px-4 py-2 text-sm md:text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {state.inviteLoading ? '發送中...' : '發送邀請'}
                    </button>
                    <button
                      onClick={() => setState(prev => ({ ...prev, showInviteForm: false, inviteEmail: '' }))}
                      className="flex-1 md:flex-none px-4 py-2 text-sm md:text-base text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      取消
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {state.activeTab === 'users' ? (
          /* Users List */
          <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200">
            <h2 className="text-base md:text-lg font-medium text-gray-900 flex items-center">
              <UsersIcon className="w-5 md:w-6 h-5 md:h-6 mr-2 text-gray-500" />
              使用者列表 ({state.users.length})
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              💡 <strong>提醒：</strong>當您變更使用者角色後，該使用者需要重新登入才能看到新的權限設定（如管理員後台等功能）。
            </p>
          </div>

          {state.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : state.error ? (
            <div className="px-6 py-12 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
              <p className="mt-1 text-sm text-gray-500">{state.error}</p>
              <button
                onClick={fetchUsers}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                重新載入
              </button>
            </div>
          ) : (
            <div>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        使用者
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        權限群組
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        狀態
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        註冊時間
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        最後登入
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        管理員備註
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {state.users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.name}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-600">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {state.editingUser === user.id && state.editingField === 'name' ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      value={state.editingValue}
                                      onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                      className="text-sm border border-gray-300 rounded px-2 py-1 w-32"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => saveEdit(user.id, 'name')}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <CheckIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <XMarkIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <span>{user.name}</span>
                                    {canEditUser(user) && (
                                      <button
                                        onClick={() => startEdit(user.id, 'name', user.name)}
                                        className="ml-2 text-gray-400 hover:text-blue-600"
                                      >
                                        <PencilIcon className="w-3 h-3" />
                                      </button>
                                    )}
                                    {user.id === currentUser.id && (
                                      <span className="ml-2 text-xs text-blue-600">(您)</span>
                                    )}
                                    {isSuperAdminUser(user) && (
                                      <span className="ml-2 text-xs text-purple-600">(超級管理員)</span>
                                    )}
                                    {user.role === 'ADMIN' && !isSuperAdminUser(user) && (
                                      <span className="ml-2 text-xs text-orange-600">(管理員)</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {state.editingUser === user.id && state.editingField === 'email' ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="email"
                                      value={state.editingValue}
                                      onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                      className="text-sm border border-gray-300 rounded px-2 py-1 w-48"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => saveEdit(user.id, 'email')}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <CheckIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <XMarkIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <span>{user.email}</span>
                                    {canEditUser(user) && (
                                      <button
                                        onClick={() => startEdit(user.id, 'email', user.email)}
                                        className="ml-2 text-gray-400 hover:text-blue-600"
                                      >
                                        <PencilIcon className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="flex flex-wrap gap-1">
                              {/* 角色標籤 */}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.role === 'SUPER_ADMIN'
                                  ? 'bg-purple-100 text-purple-800'
                                  : user.role === 'ADMIN' 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {user.role === 'SUPER_ADMIN' ? '超級管理員' : user.role === 'ADMIN' ? '管理員' : '使用者'}
                              </span>
                              
                              {/* 群組標籤 */}
                              {((user as any).groups || []).map((userGroup: any) => (
                                <span 
                                  key={userGroup.group.id} 
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    userGroup.group.isSystem 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : 'bg-green-100 text-green-800'
                                  }`}
                                >
                                  {userGroup.group.name}
                                </span>
                              ))}
                              
                              {((user as any).groups || []).length === 0 && user.role !== 'SUPER_ADMIN' && (
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                  無群組
                                </span>
                              )}
                            </div>
                            
                            {canEditUser(user) && (
                              <button
                                onClick={() => openGroupModal(user)}
                                className="text-gray-400 hover:text-blue-600 p-1"
                                title="編輯群組"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              onClick={() => canEditUser(user) && updateUserStatus(user.id, !user.enabled)}
                              disabled={!canEditUser(user)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.enabled 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              } ${canEditUser(user) ? 'hover:opacity-75 cursor-pointer' : 'cursor-not-allowed'}`}
                            >
                              {user.enabled ? (
                                <>
                                  <EyeIcon className="w-3 h-3 mr-1" />
                                  啟用
                                </>
                              ) : (
                                <>
                                  <EyeSlashIcon className="w-3 h-3 mr-1" />
                                  停用
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString('zh-TW')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.lastLoginAt 
                            ? new Date(user.lastLoginAt).toLocaleDateString('zh-TW')
                            : '從未登入'
                          }
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-xs">
                            {state.editingUser === user.id && state.editingField === 'notes' ? (
                              <div className="flex items-start space-x-2">
                                <textarea
                                  value={state.editingValue}
                                  onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 w-full resize-none"
                                  rows={3}
                                  maxLength={1000}
                                  placeholder="輸入管理員備註..."
                                  autoFocus
                                />
                                <div className="flex flex-col space-y-1">
                                  <button
                                    onClick={() => saveEdit(user.id, 'notes')}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start space-x-2">
                                <div className="flex-1 min-w-0">
                                  {user.adminNotes ? (
                                    <p className="text-sm text-gray-700 break-words">
                                      {user.adminNotes}
                                    </p>
                                  ) : (
                                    <span className="text-sm text-gray-400 italic">無備註</span>
                                  )}
                                </div>
                                <button
                                  onClick={() => startEdit(user.id, 'notes', user.adminNotes || '')}
                                  className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                                  title="編輯備註"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {canEditUser(user) ? (
                            <div className="flex space-x-2">
                              {canPromoteUser(user) && (
                                <button
                                  onClick={() => promoteToAdmin(user.id, user.name)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="提升為管理員"
                                >
                                  <ArrowUpIcon className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteUser(user.id)}
                                className="text-red-600 hover:text-red-900"
                                title="刪除使用者"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">無法編輯</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden">
                {state.users.map((user) => (
                  <div key={user.id} className="border-b border-gray-200 p-4">
                    <div className="flex items-start space-x-3">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-12 h-12 rounded-full flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-gray-600">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center">
                              {state.editingUser === user.id && state.editingField === 'name' ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={state.editingValue}
                                    onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                    className="text-sm border border-gray-300 rounded px-2 py-1 w-28"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => saveEdit(user.id, 'name')}
                                    className="text-green-600"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="text-red-600"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <p className="text-sm font-medium text-gray-900">
                                    {user.name}
                                  </p>
                                  {canEditUser(user) && (
                                    <button
                                      onClick={() => startEdit(user.id, 'name', user.name)}
                                      className="ml-2 text-gray-400 hover:text-blue-600"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                  {user.id === currentUser.id && (
                                    <span className="ml-1 text-xs text-blue-600">(您)</span>
                                  )}
                                  {isSuperAdminUser(user) && (
                                    <span className="ml-1 text-xs text-purple-600">(超級管理員)</span>
                                  )}
                                  {user.role === 'ADMIN' && !isSuperAdminUser(user) && (
                                    <span className="ml-1 text-xs text-orange-600">(管理員)</span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center">
                              {state.editingUser === user.id && state.editingField === 'email' ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="email"
                                    value={state.editingValue}
                                    onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                    className="text-xs border border-gray-300 rounded px-2 py-1 w-40"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => saveEdit(user.id, 'email')}
                                    className="text-green-600"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="text-red-600"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <p className="text-xs text-gray-500 truncate">
                                    {user.email}
                                  </p>
                                  {canEditUser(user) && (
                                    <button
                                      onClick={() => startEdit(user.id, 'email', user.email)}
                                      className="ml-2 text-gray-400 hover:text-blue-600"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {canPromoteUser(user) && (
                              <button
                                onClick={() => promoteToAdmin(user.id, user.name)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="提升為管理員"
                              >
                                <ArrowUpIcon className="w-4 h-4" />
                              </button>
                            )}
                            {canEditUser(user) && (
                              <button
                                onClick={() => deleteUser(user.id)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="刪除使用者"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-2 space-y-2">
                          {/* Groups */}
                          <div className="flex items-start space-x-2">
                            <span className="text-xs text-gray-500 mt-1">權限群組:</span>
                            <div className="flex-1">
                              <div className="flex flex-wrap gap-1">
                                {/* 角色標籤 */}
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  user.role === 'SUPER_ADMIN'
                                    ? 'bg-purple-100 text-purple-800'
                                    : user.role === 'ADMIN' 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.role === 'SUPER_ADMIN' ? '超級管理員' : user.role === 'ADMIN' ? '管理員' : '使用者'}
                                </span>
                                
                                {/* 群組標籤 */}
                                {((user as any).groups || []).map((userGroup: any) => (
                                  <span 
                                    key={userGroup.group.id} 
                                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      userGroup.group.isSystem 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}
                                  >
                                    {userGroup.group.name}
                                  </span>
                                ))}
                                
                                {((user as any).groups || []).length === 0 && user.role !== 'SUPER_ADMIN' && (
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                    無群組
                                  </span>
                                )}
                              </div>
                              
                              {canEditUser(user) && (
                                <button
                                  onClick={() => openGroupModal(user)}
                                  className="text-blue-600 hover:text-blue-700 text-xs mt-1 flex items-center"
                                >
                                  <PencilIcon className="w-3 h-3 mr-1" />
                                  編輯群組
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Status */}
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">狀態:</span>
                            <button
                              onClick={() => canEditUser(user) && updateUserStatus(user.id, !user.enabled)}
                              disabled={!canEditUser(user)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.enabled 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              } ${canEditUser(user) ? 'hover:opacity-75 cursor-pointer' : 'cursor-not-allowed'}`}
                            >
                              {user.enabled ? (
                                <>
                                  <EyeIcon className="w-3 h-3 mr-1" />
                                  啟用
                                </>
                              ) : (
                                <>
                                  <EyeSlashIcon className="w-3 h-3 mr-1" />
                                  停用
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-gray-500">
                          <div>註冊: {new Date(user.createdAt).toLocaleDateString('zh-TW')}</div>
                          <div>最後登入: {user.lastLoginAt 
                            ? new Date(user.lastLoginAt).toLocaleDateString('zh-TW')
                            : '從未登入'
                          }</div>
                        </div>
                        
                        {/* Admin Notes */}
                        <div className="mt-3 border-t border-gray-100 pt-3">
                          <div className="flex items-start justify-between">
                            <span className="text-xs font-medium text-gray-500">管理員備註:</span>
                            <button
                              onClick={() => startEdit(user.id, 'notes', user.adminNotes || '')}
                              className="text-gray-400 hover:text-blue-600 ml-2"
                              title="編輯備註"
                            >
                              <PencilIcon className="w-3 h-3" />
                            </button>
                          </div>
                          {state.editingUser === user.id && state.editingField === 'notes' ? (
                            <div className="mt-2">
                              <textarea
                                value={state.editingValue}
                                onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                                className="text-sm border border-gray-300 rounded px-2 py-1 w-full resize-none"
                                rows={3}
                                maxLength={1000}
                                placeholder="輸入管理員備註..."
                                autoFocus
                              />
                              <div className="flex justify-between items-center mt-2">
                                <span className="text-xs text-gray-400">{state.editingValue.length}/1000</span>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => saveEdit(user.id, 'notes')}
                                    className="text-green-600 hover:text-green-900 text-sm"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="text-red-600 hover:text-red-900 text-sm"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1">
                              {user.adminNotes ? (
                                <p className="text-xs text-gray-600 break-words whitespace-pre-wrap">
                                  {user.adminNotes}
                                </p>
                              ) : (
                                <span className="text-xs text-gray-400 italic">無備註</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {state.users.length === 0 && !state.loading && (
                <div className="text-center py-12">
                  <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">沒有使用者</h3>
                  <p className="mt-1 text-sm text-gray-500">開始邀請使用者加入系統</p>
                </div>
              )}
            </div>
          )}
        </div>
        ) : state.activeTab === 'groups' ? (
          /* Group Management */
          <GroupManagement />
        ) : state.activeTab === 'projects' ? (
          /* Project Management */
          <ProjectManagement />
        ) : state.activeTab === 'custom-fields' ? (
          /* Custom Field Management */
          <CustomFieldManagement />
        ) : state.activeTab === 'audit-logs' ? (
          /* Audit Log Management */
          <AuditLogManagement />
        ) : state.activeTab === 'queues' ? (
          /* Queue Management */
          <QueueManagement />
        ) : (
          /* Role Management */
          <RoleManagement />
        )}
      </div>

      {/* Group Assignment Modal */}
      {state.showGroupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  編輯使用者群組
                </h3>
                <button
                  onClick={closeGroupModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  選擇要指派給使用者的權限群組：
                </div>
                
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {state.groupsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">載入群組中...</span>
                    </div>
                  ) : (state.availableGroups || []).map((group: any) => (
                    <label key={group.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={state.selectedGroupIds.includes(group.id)}
                        onChange={() => toggleGroupSelection(group.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{group.name}</span>
                          {group.isSystem && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              系統群組
                            </span>
                          )}
                        </div>
                        {group.description && (
                          <p className="text-xs text-gray-500 mt-1">{group.description}</p>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {group._count?.permissions || 0} 項權限 · {group._count?.users || 0} 位成員
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeGroupModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={updateUserGroups}
                  disabled={state.groupsLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {state.groupsLoading ? '更新中...' : '確認更新'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  )
}

export default AdminPanel
